import {
  accessoryCategories,
  ductedAccessoryCategories,
  ductlessAccessoryCategories
} from 'data/constants';
import {
  AccessoryCategoryType,
  AccessoryGroupType,
  AccessoryProductType,
  AccessoryWithQuantityType,
  CondenserType,
  SelectedDuctedSystemType,
  SelectedSystemType,
  SystemOptionCondenserType
} from 'data/types';
import { combineProductQuantities } from 'utils/products';

export function getAvailableAccessoriesForCondenser(
  condenser: SystemOptionCondenserType | CondenserType
): AccessoryProductType[] {
  return [
    ...(condenser.selectedProduct?.accessories ?? []),
    ...condenser.zones.flatMap((z) => z.selectedProduct?.accessories ?? [])
  ];
}

export function getApplicableCategoriesForCondenser(
  condenser: SystemOptionCondenserType | CondenserType,
  baseCategories:
    | typeof ductlessAccessoryCategories
    | typeof ductedAccessoryCategories
): AccessoryCategoryType[] {
  const availableAccessories = getAvailableAccessoriesForCondenser(condenser);

  return (baseCategories as AccessoryCategoryType[]).filter((category) =>
    availableAccessories.some(
      (acc) =>
        accessoryCategories[category]?.includes(acc.type) && acc.type !== 'wire'
    )
  );
}

export function condenserHasSelectableLineSets(
  condenser: SystemOptionCondenserType | CondenserType
): boolean {
  return getAvailableAccessoriesForCondenser(condenser).some(
    (acc) => acc.type === 'line_set' || acc.type === 'wire'
  );
}

export function getDuctlessAccessoryRequirements(
  system: SelectedSystemType
): boolean[] {
  const requirements: boolean[] = [];

  system.configuration.condensers.forEach((condenser) => {
    const applicableCategories = getApplicableCategoriesForCondenser(
      condenser,
      ductlessAccessoryCategories
    );

    applicableCategories.forEach((category) => {
      requirements.push(
        system.noAccessoriesNeeded ||
          condenser.noAccessoriesNeeded ||
          condenser.accessoriesCompleted.includes(category) ||
          condenser.accessoriesNotNeeded.includes(category)
      );
    });

    if (condenserHasSelectableLineSets(condenser)) {
      requirements.push(
        system.noAccessoriesNeeded ||
          system.accessoriesCompleted.includes('lineSet') ||
          system.accessoriesNotNeeded.includes('lineSet')
      );
    }

    if (condenser.zones.some((z) => z.selectedProduct?.type === 'Multi Room')) {
      requirements.push(
        system.noAccessoriesNeeded ||
          system.accessoriesCompleted.includes('ductingKit') ||
          system.accessoriesNotNeeded.includes('ductingKit')
      );
    }
  });

  return requirements;
}

export function getDuctedAccessoryRequirements(
  system: SelectedDuctedSystemType
): boolean[] {
  const requirements: boolean[] = [];

  system.config.condensers.forEach((condenser) => {
    const applicableCategories = getApplicableCategoriesForCondenser(
      condenser,
      ductedAccessoryCategories
    );

    applicableCategories.forEach((category) => {
      requirements.push(
        system.noAccessoriesNeeded ||
          condenser.noAccessoriesNeeded ||
          condenser.accessoriesCompleted.includes(category) ||
          condenser.accessoriesNotNeeded.includes(category)
      );
    });

    if (
      condenser.type === 'ductless' &&
      condenserHasSelectableLineSets(condenser)
    ) {
      requirements.push(
        system.noAccessoriesNeeded ||
          system.accessoriesCompleted.includes('lineSet') ||
          system.accessoriesNotNeeded.includes('lineSet')
      );
    }
  });

  return requirements;
}

export function getCondenserGroupCategories(
  condenser: SystemOptionCondenserType | CondenserType,
  groups: AccessoryGroupType[]
): AccessoryCategoryType[] {
  const condenserAirHandlerNames = condenser.zones.map(
    (zone) => zone.selectedProduct?.name
  );

  return groups
    .filter((group) =>
      group.accessories.some(
        (acc) =>
          acc.compatibleProducts.includes(
            condenser.selectedProduct?.name ?? ''
          ) ||
          acc.compatibleProducts.some((p) =>
            condenserAirHandlerNames.includes(p)
          )
      )
    )
    .map((group) => group.category);
}

export function getQuotableAccessories(
  system: SelectedSystemType | SelectedDuctedSystemType
): AccessoryWithQuantityType[] {
  const configuration =
    'config' in system ? system.config : system.configuration;

  return combineProductQuantities([
    ...system.selectedAccessories.filter(
      (acc) =>
        !system.accessoriesNotNeeded.some((cat) =>
          accessoryCategories[cat]?.includes(acc.type)
        ) && !system.noAccessoriesNeeded
    ),
    ...configuration.condensers.flatMap((cond) =>
      cond.selectedAccessories.filter(
        (acc) =>
          !cond.accessoriesNotNeeded.some((cat) =>
            accessoryCategories[cat]?.includes(acc.type)
          ) && !cond.noAccessoriesNeeded
      )
    )
  ]);
}
